import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface EmailDataPageState {
  data: any[];
  lastUpdateTime?: string;
  loading: boolean;
  error: string | null;
  searchFilter: string;
  sortConfig: {
    fieldName: string;
    direction: "asc" | "desc";
  };
  selectedRowId: string | null;
  dataPagination: {
    hasMore: boolean;
    offset: number;
    limit: number;
    totalCount: number;
    loadingMore: boolean;
  };
  searchPagination: {
    hasMore: boolean;
    offset: number;
    limit: number;
    totalCount: number;
    loadingMore: boolean;
  };
}

interface EmailDataPagesState {
  dataSlice: {
    [key: string]: EmailDataPageState;
  };
  selectedThreadId: string | null;
  selectedMessageId: string | null;
}

const sortData = (
  data: any[],
  fieldName: string,
  direction: "asc" | "desc"
) => {
  return [...data].sort((a, b) => {
    const aValue = a[fieldName];
    const bValue = b[fieldName];
    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

const initialState: EmailDataPagesState = {
  dataSlice: {},
  selectedThreadId: null,
  selectedMessageId: null,
};

const emailDataPageSlice = createSlice({
  name: "emailDataPages",
  initialState,
  reducers: {
    initializeDataSlice: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (!state.dataSlice[key]) {
        state.dataSlice[key] = {
          data: [],
          lastUpdateTime: undefined,
          loading: true,
          error: null,
          searchFilter: "",
          sortConfig: { fieldName: "date", direction: "desc" },
          selectedRowId: null,
          dataPagination: {
            hasMore: false,
            offset: 0,
            limit: 200,
            totalCount: 0,
            loadingMore: false,
          },
          searchPagination: {
            hasMore: false,
            offset: 0,
            limit: 200,
            totalCount: 0,
            loadingMore: false,
          },
        };
      }
    },
    setData: (
      state,
      action: PayloadAction<{
        key: string;
        data: any[];
        lastUpdateTime?: string;
      }>
    ) => {
      const { key, data, lastUpdateTime } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) {
        pageState.data = sortData(
          data,
          pageState.sortConfig.fieldName,
          pageState.sortConfig.direction
        );
        pageState.lastUpdateTime = lastUpdateTime;
      }
    },
    setLoading: (
      state,
      action: PayloadAction<{ key: string; loading: boolean }>
    ) => {
      const { key, loading } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) pageState.loading = loading;
    },
    setError: (
      state,
      action: PayloadAction<{ key: string; error: string | null }>
    ) => {
      const { key, error } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) pageState.error = error;
    },
    setSearchFilter: (
      state,
      action: PayloadAction<{ key: string; filter: string }>
    ) => {
      const { key, filter } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) pageState.searchFilter = filter;
    },
    setSortConfig: (
      state,
      action: PayloadAction<{
        key: string;
        config: { fieldName: string; direction: "asc" | "desc" };
      }>
    ) => {
      const { key, config } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) {
        pageState.sortConfig = config;
        pageState.data = sortData(
          pageState.data,
          config.fieldName,
          config.direction
        );
      }
    },
    setSelectedMessage: (
      state,
      action: PayloadAction<{
        threadId: string | null;
        messageId: string | null;
      }>
    ) => {
      const { messageId, threadId } = action.payload;
      console.log(
        `selected message messageId: ${messageId} threadId: ${threadId}`
      );
      state.selectedMessageId = messageId;
      state.selectedThreadId = threadId;
    },
    setSelectedRowId: (
      state,
      action: PayloadAction<{ key: string; rowId: string | null }>
    ) => {
      const { key, rowId } = action.payload;
      for (const [, slice] of Object.entries(state.dataSlice)) {
        slice.selectedRowId = null;
      }
      const slice = state.dataSlice[key];
      if (slice) slice.selectedRowId = rowId;
    },
    setDataPagination: (
      state,
      action: PayloadAction<{
        key: string;
        pagination: Partial<EmailDataPageState["dataPagination"]>;
      }>
    ) => {
      const { key, pagination } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) {
        pageState.dataPagination = {
          ...pageState.dataPagination,
          ...pagination,
        };
      }
    },
    setSearchPagination: (
      state,
      action: PayloadAction<{
        key: string;
        pagination: Partial<EmailDataPageState["searchPagination"]>;
      }>
    ) => {
      const { key, pagination } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) {
        pageState.searchPagination = {
          ...pageState.searchPagination,
          ...pagination,
        };
      }
    },
    appendData: (
      state,
      action: PayloadAction<{
        key: string;
        data: any[];
        isSearchData?: boolean;
      }>
    ) => {
      const { key, data, isSearchData } = action.payload;
      const pageState = state.dataSlice[key];
      if (pageState) {
        // Remove duplicates by id
        const existingIds = new Set(pageState.data.map((item) => item.id));
        const newItems = data.filter((item) => !existingIds.has(item.id));
        pageState.data = [...pageState.data, ...newItems];

        // Update the appropriate pagination offset
        if (isSearchData) {
          pageState.searchPagination.offset = pageState.data.length;
        } else {
          pageState.dataPagination.offset = pageState.data.length;
        }
      }
    },
  },
});

export const {
  initializeDataSlice,
  setData,
  setLoading,
  setError,
  setSearchFilter,
  setSortConfig,
  setSelectedMessage,
  setSelectedRowId,
  setDataPagination,
  setSearchPagination,
  appendData,
} = emailDataPageSlice.actions;

export default emailDataPageSlice.reducer;
